import React, { useState, useEffect } from 'react';
import { exportMealPlanToPDF } from './services/pdfExport';
import { createPortalSession } from './services/api';
import {
  ShoppingCart, FileDown, Camera, LogOut, CheckCircle, Loader2, Trash2, Plus, Minus, X, Star,
  Bookmark, History, Package, Target, Sun, Moon, Wine, Scan, ShieldCheck,
  Zap, Save, Lock, Menu, User,
  CreditCard, Heart, BookOpen, RefreshCcw, Mail, UtensilsCrossed, Crown, MessageSquareQuote
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { useProfile } from './hooks/useProfile';
import { useUI } from './hooks/useUI';
import { useMacros } from './hooks/useMacros';
import { useInventory } from './hooks/useInventory';
import { useWine } from './hooks/useWine';
import { useMealPlan } from './hooks/useMealPlan';
import { MESSAGES } from './constants/messages';
import Login from './components/Login';
import CommandCenter from './components/CommandCenter';
import { VarietyModeSelector } from './components/VarietyModeSelector';
import { DietPhilosophySelector } from './components/DietPhilosophySelector';
import { MacroProfileSelector } from './components/MacroProfileSelector';
import { DietVerificationBadge } from './components/DietVerificationBadge';
import CommitBar from './components/CommitBar';
import ManualOverridePanel from './components/ManualOverridePanel';
import GuideModal from './components/GuideModal';
import Contact from './components/Contact';
import { useAppContextStore } from './stores/appContextStore';
import { useEntitlementPolling } from './hooks/useEntitlementPolling';

const MACRO_PRESETS = [
  { name: 'Balanced', label: 'Balanced', carbs: 40, protein: 30, fat: 30, icon: '⚖️', color: 'text-blue-500' },
  { name: 'High Protein', label: 'High Protein', carbs: 25, protein: 45, fat: 30, icon: '💪', color: 'text-emerald-500' },
  { name: 'Keto', label: 'Keto', carbs: 5, protein: 25, fat: 70, icon: '🥑', color: 'text-amber-500' },
];

const UpgradeModal = ({ isOpen, onClose, featureName, onUpgrade }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-md" onClick={onClose} />
      <div className="relative w-full max-w-md bg-[var(--glass-surface)] border border-[var(--glass-border)] rounded-[2.5rem] shadow-2xl overflow-hidden animate-pop-in">
        <div className="p-8 text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl mx-auto mb-6 flex items-center justify-center shadow-xl shadow-blue-500/30">
            <Zap className="w-10 h-10 text-white fill-current" />
          </div>

          <h3 className="text-2xl font-black text-[var(--text-primary)] mb-2">
            Quota Reached!
          </h3>
          <p className="text-[var(--text-secondary)] font-medium mb-8 leading-relaxed">
            You've hit the monthly limit for <strong>{featureName}</strong> on your current plan. Upgrade to a higher tier for more power!
          </p>

          <div className="space-y-3">
            <a
              href="https://cathys-sous-chef.com/#pricing"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full bg-amber-500 hover:bg-amber-600 text-white font-black py-4 rounded-2xl shadow-lg shadow-amber-500/20 transition-all active:scale-95 flex items-center justify-center gap-2"
            >
              <Crown className="w-5 h-5" />
              Upgrade on Website
            </a>
            <button
              onClick={onClose}
              className="w-full bg-transparent hover:bg-[var(--text-secondary)]/5 text-[var(--text-secondary)] font-bold py-3 rounded-2xl transition-all"
            >
              Maybe Later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const WineEditModal = ({ isOpen, onClose, wine, onSave, isProcessing }) => {
  const [formData, setFormData] = React.useState(wine || {});

  React.useEffect(() => {
    if (wine) setFormData(wine);
  }, [wine]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-[var(--glass-surface)] border border-[var(--glass-border)] rounded-3xl shadow-2xl overflow-hidden animate-pop-in">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-black text-[var(--text-primary)] flex items-center gap-2">
              <Wine className="w-6 h-6 text-[var(--accent-color)]" />
              Edit Bottle
            </h3>
            <button onClick={onClose} className="p-2 hover:bg-[var(--text-secondary)]/10 rounded-full transition-colors">
              <X className="w-5 h-5 text-[var(--text-secondary)]" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wider mb-1">Name</label>
              <input
                type="text"
                placeholder="e.g. Napa Valley Cabernet"
                value={formData.name || ''}
                onChange={e => setFormData({ ...formData, name: e.target.value })}
                className="w-full bg-[var(--bg-secondary)] border-none rounded-xl px-4 py-3 text-[var(--text-primary)] font-bold focus:ring-2 focus:ring-[var(--accent-color)] transition-all"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wider mb-1">Varietal</label>
                <input
                  type="text"
                  placeholder="e.g. Cabernet Sauvignon"
                  value={formData.varietal || ''}
                  onChange={e => setFormData({ ...formData, varietal: e.target.value })}
                  className="w-full bg-[var(--bg-secondary)] border-none rounded-xl px-4 py-3 text-[var(--text-primary)] font-medium focus:ring-2 focus:ring-[var(--accent-color)] transition-all"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wider mb-1">Region</label>
                <input
                  type="text"
                  placeholder="e.g. Napa Valley"
                  value={formData.region || ''}
                  onChange={e => setFormData({ ...formData, region: e.target.value })}
                  className="w-full bg-[var(--bg-secondary)] border-none rounded-xl px-4 py-3 text-[var(--text-primary)] font-medium focus:ring-2 focus:ring-[var(--accent-color)] transition-all"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wider mb-1">Vintage</label>
                <input
                  type="number"
                  value={formData.vintage || ''}
                  onChange={e => setFormData({ ...formData, vintage: parseInt(e.target.value) || null })}
                  className="w-full bg-[var(--bg-secondary)] border-none rounded-xl px-4 py-3 text-[var(--text-primary)] font-medium focus:ring-2 focus:ring-[var(--accent-color)] transition-all"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wider mb-1">Quantity</label>
                <input
                  type="number"
                  value={formData.quantity || 1}
                  onChange={e => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })}
                  min="1"
                  className="w-full bg-[var(--bg-secondary)] border-none rounded-xl px-4 py-3 text-[var(--text-primary)] font-medium focus:ring-2 focus:ring-[var(--accent-color)] transition-all"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wider mb-1">Type</label>
              <select
                value={formData.wine_type || 'Red'}
                onChange={e => setFormData({ ...formData, wine_type: e.target.value })}
                className="w-full bg-[var(--bg-secondary)] border-none rounded-xl px-4 py-3 text-[var(--text-primary)] font-medium focus:ring-2 focus:ring-[var(--accent-color)] transition-all appearance-none"
              >
                <option value="Red">Red</option>
                <option value="White">White</option>
                <option value="Rosé">Rosé</option>
                <option value="Sparkling">Sparkling</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wider mb-1">Sommelier Notes</label>
              <textarea
                value={formData.sommelier_notes || ''}
                onChange={e => setFormData({ ...formData, sommelier_notes: e.target.value })}
                rows={3}
                className="w-full bg-[var(--bg-secondary)] border-none rounded-xl px-4 py-3 text-[var(--text-primary)] text-sm font-medium focus:ring-2 focus:ring-[var(--accent-color)] transition-all resize-none"
                placeholder="Sommelier notes or your own tasting notes..."
              />
            </div>
          </div>

          <button
            onClick={() => onSave(formData)}
            disabled={isProcessing}
            className="w-full mt-6 bg-[var(--accent-color)] hover:brightness-110 text-white font-bold py-4 rounded-xl shadow-lg shadow-blue-500/30 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center gap-2"
          >
            {isProcessing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

function MealPlanner() {
  const { currentUser, logout, forceTokenRefresh } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isOverridePanelOpen, setIsOverridePanelOpen] = useState(false);
  const [isGuideOpen, setIsGuideOpen] = useState(false);

  // ARCH-001: UI State Store
  const ui = useUI();
  const {
    view, setView,
    isDrawerOpen, setIsDrawerOpen,
    isDarkMode, setIsDarkMode,
    showToast, triggerToast,
    fadingId, setFadingId,
    isUpgradeModalOpen, setIsUpgradeModalOpen,
    upgradeFeature,
    actionLoading, setActionLoading,
  } = ui;

  // ARCH-001: Macros & Exclusions Store
  const macrosHook = useMacros(currentUser);
  const {
    macros,
    tempMacros, setTempMacros,
    exclusions,
    newExclusion, setNewExclusion,
    setActivePreset,
    handleUpdateMacros,
    handleAddExclusion,
    handleRemoveExclusion,
    handlePresetSelect
  } = macrosHook;

  // ARCH-001: Inventory Store
  const inventoryHook = useInventory(currentUser);
  const {
    inventory,
    inventoryDraft, setInventoryDraft,
    newItem, setNewItem,
    isAdding,
    updatedItems,
    handleAddItem: originalHandleAddItem,
    handleDeleteItem,
    processScan,
    handleStockKitchen: originalHandleStockKitchen,
    removeFromDraft: removeInventoryFromDraft,
    fadingInventoryItem
  } = inventoryHook;

  // ARCH-001: Wine Cellar Store
  const wineHook = useWine(currentUser);
  const {
    wineCellar,
    wineDraft, setWineDraft,
    editingWine, setEditingWine,
    isSavingWine,
    isWineScanning,
    wineUpdateLoading,
    newWine, setNewWine,
    isAddingWine,
    handleAddWine: originalHandleAddWine,
    handleDeleteWine,
    handleSaveWine: originalHandleSaveWine,
    handleStockCellar: originalHandleStockCellar,
    handleWineScan: storeHandleWineScan,
    removeFromDraft: removeWineFromDraft
  } = wineHook;
  const mealPlanHook = useMealPlan(currentUser, macrosHook, inventoryHook, ui);
  const {
    currentPlan,
    history,
    favorites,
    selectedMeal,
    chefMessage,
    isLoading: mealPlanLoading,
    handleCommand: originalHandleCommand,
    handleToggleFavorite: originalHandleToggleFavorite,
    handleDeleteHistory,
    handleCommitPlan,
    loadPlanDetails,
    loadFavoriteDetails,
    setSelectedMeal,
    setCurrentPlan,
    setChefMessage,
    toggleWineSelection,
  } = mealPlanHook;

  // FEAT-009: Reactive lookup for the selected meal to ensure modal is always in sync with plan updates
  const liveSelectedMeal = React.useMemo(() => {
    if (!selectedMeal) return null;
    const found = currentPlan?.meals?.find(m =>
      String(m.day) === String(selectedMeal.day) && m.meal_type === selectedMeal.meal_type
    );
    return found;
  }, [selectedMeal, currentPlan?.meals]);

  const prefetch = useAppContextStore(state => state.prefetch);
  const isContextLoading = useAppContextStore(state => state.isLoading);

  // SUBSCRIPTION UPGRADE HANDLER
  const { startPolling } = useEntitlementPolling(
    useAppContextStore.getState().cachedContext?.profile?.entitlement_version,
    (newData) => {
      triggerToast("Upgrade Successful! Welcome to Pro.");
      // AppContextStore is already updated via polling hook's internal fetch
    },
    () => { // Optional: onSignup callback
      ui.triggerToast("Payment successful! Please create your account to begin.");
    }
  );

  useEffect(() => {
    window.onViewChange = (v) => setView(v);
    return () => { delete window.onViewChange; };
  }, [setView]);

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'success' && currentUser) {
      const handleUpgradeSync = async () => {
        try {
          // 1. Initial attempt: Force refresh the JWT to pick up new 'tier' custom claim
          const token = await forceTokenRefresh();
          // 2. Prefetch new context with the updated token
          if (token) await prefetch(token);

          // 3. Fallback: If still not reflected, start the polling Success Bridge
          const currentTier = useAppContextStore.getState().cachedContext?.profile?.tier;
          if (currentTier === 'free') {
            ui.triggerToast("Processing your upgrade...");
            startPolling();
          } else {
            ui.triggerToast("Upgrade Successful! Welcome to Pro.");
          }
        } catch (err) {
          console.error("Upgrade sync failed:", err);
        }
      };

      handleUpgradeSync();
      // Clean URL without reload
      window.history.replaceState({}, document.title, "/");
    } else if (params.get('mode') === 'signup' && params.get('payment') === 'success') {
      // User just paid via public site, show a welcome message and stay on login
      ui.triggerToast(`Welcome! Your ${params.get('tier')} subscription is ready. Please sign up to start.`);
      window.history.replaceState({}, document.title, "/");
    }
  }, [currentUser, startPolling, forceTokenRefresh, prefetch, ui]);

  const handleAddWine = async () => {
    await originalHandleAddWine();
  };

  const handleSaveWine = async (updatedData) => {
    try {
      await originalHandleSaveWine(updatedData);
      if (updatedData.id) {
        setChefMessage("Bottle updated in your cellar.");
      }
    } catch (err) {
      setChefMessage("Oops, I couldn't save those changes.");
    }
  };

  const handleStockCellar = async () => {
    setLoading(true);
    setChefMessage("Stocking your cellar, sommelier...");
    try {
      await originalHandleStockCellar();
      setChefMessage("Your cellar has been updated!");
    } catch (err) {
      setChefMessage("I had trouble updating your digital cellar.");
    } finally {
      setLoading(false);
    }
  };

  const handleWineScan = async (file) => {
    try {
      setChefMessage("Reading that label for you, sommelier...");

      const compressed = await import('./utils/imageUtils').then(m => m.compressImage(file));
      await storeHandleWineScan(compressed);

      setChefMessage("I've identified some bottles! Review them before they go in the cellar.");
    } catch (err) {
      console.error(err);
      setChefMessage("I had trouble reading that wine label.");
    }
  };

  const handleAddItem = async () => {
    await originalHandleAddItem();
  };

  const handleStockKitchen = async () => {
    setLoading(true);
    setChefMessage("Stocking your kitchen...");
    try {
      await originalHandleStockKitchen();
      setChefMessage("Pantry updated. What should we cook with these?");
    } catch (err) {
      setChefMessage("Oh no, I couldn't update the kitchen inventory.");
    } finally {
      setLoading(false);
    }
  };

  // FEAT-009: Wine pairing toggle -> Delegated to Store (ARCH-FIX)
  const handleWineToggle = async (meal) => {
    if (!currentPlan?.id) return;
    try {
      await toggleWineSelection(await forceTokenRefresh(), meal);
      triggerToast(meal.wine_pairing?.selected === false ? "Wine included!" : "Wine excluded!");
    } catch (err) {
      setChefMessage("Sorry, I couldn't update the wine selection.");
      triggerToast("Failed to update wine.");
    }
  };

  // ARCH-001: Use profile store via hook (replaces inline state)
  const {
    userTier,
    userName,
    toggles,
    varietyMode,
    varietyModeSaving,
    wineSettings,
    mealSettings,
    dietPhilosophy,
    macroProfile,
    householdSize, // V13
    profileLoading, // V6.1-P9: Destructure loading state
    handleVarietyModeChange,
    handleTogglePreference,
    handleMealSettingToggle,
    handleWineSettingToggle,
    handleDietPhilosophyChange,
    handleMacroProfileChange: originalHandleMacroProfileChange,
    handleHouseholdSizeChange, // V13
  } = useProfile(currentUser, triggerToast);

  const handleMacroProfileChange = async (prof) => {
    // 1. Update the profile toggle identifier (Balanced, High Protein, Keto)
    await originalHandleMacroProfileChange(prof);

    // 2. If a profile was selected, find the preset and update the numeric goals
    if (!prof) {
      setActivePreset(null);
    } else {
      // PROFILES in MacroProfileSelector are lowercase IDs, but prof here is likely the display name
      // and MACRO_PRESETS in App.js uses Display Names.
      // Let's check consistency. MacroProfileSelector uses lowercase 'balanced', 'high_protein', 'keto'.
      // useProfile likely set high-level profile state.
      const preset = MACRO_PRESETS.find(p => p.name.toLowerCase().replace(' ', '_') === prof.toLowerCase() || p.name === prof);
      if (preset) {
        handlePresetSelect(preset);
      }
    }
  };

  // ARCH-001: Use profile store via hook (replaces inline state)

  // Theme Toggle Effect
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  // ARCH-001: Profile toggles and basic data loading handled by specialized hooks
  // Initial data loading on mount or user change
  // V6.1-P9: Pre-fetch ALL data via AppContext consolidated endpoint
  const setupSyncListener = useAppContextStore(state => state.setupSyncListener);
  const cleanupSyncListener = useAppContextStore(state => state.cleanupSyncListener);

  useEffect(() => {
    if (currentUser) {
      currentUser.getIdToken().then(token => {
        prefetch(token);
        setupSyncListener(currentUser.uid, token);
      });
    } else {
      cleanupSyncListener();
    }
  }, [currentUser, prefetch, setupSyncListener, cleanupSyncListener]);

  // Sync tempMacros when entering macros view
  useEffect(() => {
    if (view === 'macros') {
      setTempMacros({ ...macros });
    }
  }, [view, macros, setTempMacros]);

  /**
   * THE UNIFIED HANDLER
   */
  const handleCommand = async (command) => {
    await originalHandleCommand(command, mealSettings, dietPhilosophy, macroProfile, householdSize);
  };

  const handleUnifiedScan = async (files) => {
    if (view === 'wine') {
      handleWineScan(files[0]);
    } else {
      setChefMessage("Analyzing your groceries...");
      setLoading(true);
      try {
        await processScan(files);
        setChefMessage("I've found some items! Review them below before we stock up.");
      } catch (err) {
        setChefMessage("Oops, I had trouble reading those photos.");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleToggleFavorite = async (meal) => {
    await originalHandleToggleFavorite(meal);
  };

  const loadHistoricalPlan = async (plan) => {
    try {
      // Side-fetch full plan data if we only have metadata
      let fullPlan = plan;
      if (!plan.plan_data?.meals || plan.plan_data.meals.length === 0) {
        const token = await currentUser.getIdToken();
        fullPlan = await loadPlanDetails(token, plan.id);
      }

      setCurrentPlan({
        ...fullPlan.plan_data,
        id: fullPlan.id,
        status: fullPlan.status,
        title: fullPlan.plan_data.title || fullPlan.title,
        description: fullPlan.plan_data.description || fullPlan.description,
        meals: fullPlan.plan_data.meals || [],
        shopping_list: fullPlan.plan_data.shopping_list || []
      });
      setView('current');
    } catch (err) {
      triggerToast("Failed to load plan details.");
    }
  };

  const wrappedHandleDeleteHistory = async (e, planId) => {
    e.stopPropagation();
    setFadingId(planId);
    await new Promise(r => setTimeout(r, 300));
    setActionLoading(planId);
    try {
      await handleDeleteHistory(planId);
    } finally {
      setActionLoading(null);
      setFadingId(null);
    }
  };
  const mealTypeOrder = { 'Breakfast': 1, 'Lunch': 2, 'Snack': 3, 'Dinner': 4 };

  const groupedMeals = (currentPlan?.meals || []).reduce((acc, meal) => {
    const day = meal.day || 'Plan';
    if (!acc[day]) acc[day] = [];
    acc[day].push(meal);
    return acc;
  }, {});

  // Sort meals within each day by meal type
  Object.keys(groupedMeals).forEach(day => {
    groupedMeals[day].sort((a, b) => {
      const orderA = mealTypeOrder[a.meal_type] || 99;
      const orderB = mealTypeOrder[b.meal_type] || 99;
      return orderA - orderB;
    });
  });

  // Get sorted day keys (Day 1, Day 2, etc.)
  const sortedDays = Object.keys(groupedMeals).sort((a, b) => {
    const numA = parseInt(a.replace(/\D/g, '')) || 0;
    const numB = parseInt(b.replace(/\D/g, '')) || 0;
    return numA - numB;
  });

  const handleCommitWithOverrides = async (overrides = [], skipWineDeductions = []) => {
    if (!currentPlan.id) {
      triggerToast("Cannot commit: No plan ID found.");
      return;
    }
    setLoading(true);
    try {
      await handleCommitPlan(currentPlan.id, overrides, skipWineDeductions);
      setIsOverridePanelOpen(false);
    } catch (e) {
      // Handled in store/hook
    } finally {
      setLoading(false);
    }
  };

  const handleCommit = async () => {
    await handleCommitWithOverrides([]);
  };


  return (
    <div className="min-h-screen font-sans text-[var(--text-primary)] overflow-x-hidden transition-colors duration-500 selection:bg-[var(--accent-color)]/30">
      {/* SIDE DRAWER (PANTRY & SETTINGS) */}
      <div
        className={`fixed inset-y-4 left-4 w-80 glass-panel rounded-[2rem] z-[100] transform transition-all duration-500 ease-[cubic-bezier(0.32,0.725,0,1)] ${isDrawerOpen ? 'translate-x-0' : '-translate-x-[120%]'}`}
      >
        <div className="h-full flex flex-col p-8">
          <div className="flex justify-between items-center mb-10">
            <h2 className="text-xl font-bold flex items-center gap-2 text-[var(--text-primary)]">
              <span className="p-2 bg-[var(--accent-color)]/10 rounded-xl text-[var(--accent-color)]">
                <Target className="w-5 h-5" />
              </span>
              Settings
            </h2>
            <button onClick={() => setIsDrawerOpen(false)} className="p-2 hover:bg-[var(--text-secondary)]/10 rounded-full text-[var(--text-secondary)] transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 space-y-4">
            {/* Sidebar content - Macro button removed, keeping structure for future */}

            {/* User Profile Card - V6.1-P9: Loading Skeleton */}
            {profileLoading ? (
              <div className="bg-[var(--glass-surface)] border border-[var(--glass-border)] rounded-2xl p-4 mb-2 animate-pulse">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-[var(--text-secondary)]/10" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-[var(--text-secondary)]/10 rounded w-24" />
                    <div className="h-3 bg-[var(--text-secondary)]/10 rounded w-16" />
                  </div>
                </div>
              </div>
            ) : userName && (
              <div className="bg-[var(--glass-surface)] border border-[var(--glass-border)] rounded-2xl p-4 mb-2">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg font-black ${userTier === 'unlimited' ? 'bg-purple-500 text-white' : userTier === 'premium' ? 'bg-amber-500 text-white' : userTier === 'pro' ? 'bg-blue-500 text-white' : 'bg-gray-500/20 text-[var(--text-secondary)]'}`}>
                    {userName.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h3 className="font-bold text-[var(--text-primary)] leading-none">{userName}</h3>
                    <div className="flex items-center gap-1 mt-1">
                      {userTier === 'unlimited' ? <Zap className="w-3 h-3 text-purple-500" /> : userTier === 'premium' ? <Zap className="w-3 h-3 fill-amber-500 text-amber-500" /> : userTier === 'pro' ? <Star className="w-3 h-3 text-blue-500 fill-blue-500" /> : <ShieldCheck className="w-3 h-3 text-gray-500" />}
                      <span className={`text-[10px] font-black uppercase tracking-widest ${userTier === 'unlimited' ? 'text-purple-500' : userTier === 'premium' ? 'text-amber-500' : userTier === 'pro' ? 'text-blue-500' : 'text-gray-500'}`}>
                        {userTier} Plan
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* UPGRADE CTA (Only for Free/Pro) */}
            {(userTier === 'free' || userTier === 'pro') && (
              <a
                href="https://cathys-sous-chef.com/#pricing"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full bg-gradient-to-r from-amber-500 to-orange-600 text-white p-4 rounded-2xl shadow-lg shadow-orange-500/20 flex items-center justify-center gap-2 hover:brightness-110 transition-all active:scale-95 group mb-4"
              >
                <Crown className="w-5 h-5 fill-white group-hover:rotate-12 transition-transform" />
                <div className="text-left">
                  <div className="font-black text-sm uppercase tracking-wide">Upgrade Plan</div>
                  <div className="text-[10px] font-medium opacity-90">Unlock Premium Features</div>
                </div>
              </a>
            )}

            {/* KITCHEN GUIDE (New Feature) */}
            <button
              onClick={() => { setIsDrawerOpen(false); setIsGuideOpen(true); }}
              className="w-full flex items-center gap-4 p-4 hover:bg-[var(--accent-color)]/10 rounded-2xl transition-all group"
            >
              <div className="p-3 bg-[var(--text-secondary)]/10 text-[var(--text-secondary)] rounded-xl group-hover:bg-[var(--accent-color)]/20 group-hover:text-[var(--accent-color)] transition-colors">
                <BookOpen className="w-5 h-5" />
              </div>
              <div className="text-left">
                <div className="font-bold text-sm text-[var(--text-primary)]">Kitchen Guide</div>
                <div className="text-[10px] text-[var(--text-secondary)] uppercase font-black tracking-widest">Mastering the App</div>
              </div>
            </button>

            <button
              onClick={async () => {
                try {
                  const token = await currentUser.getIdToken();
                  const { url } = await createPortalSession(token);
                  window.location.href = url;
                } catch (err) {
                  // No subscription found, redirect to pricing
                  window.open('https://cathys-sous-chef.com/#pricing', '_blank');
                }
              }}
              className="w-full flex items-center gap-4 p-4 hover:bg-[var(--accent-color)]/10 rounded-2xl transition-all group"
            >
              <div className="p-3 bg-[var(--accent-color)]/10 text-[var(--accent-color)] rounded-xl group-hover:bg-[var(--accent-color)]/20">
                <CreditCard className="w-5 h-5" />
              </div>
              <div className="text-left">
                <div className="font-bold text-sm text-[var(--text-primary)]">Manage Subscription</div>
                <div className="text-[10px] text-[var(--text-secondary)] uppercase font-black tracking-widest">Billing & Plan Changes</div>
              </div>
            </button>

            <button
              onClick={() => { setView('contact'); setIsDrawerOpen(false); }}
              className="w-full flex items-center gap-4 p-4 hover:bg-[var(--accent-color)]/10 rounded-2xl transition-all group"
            >
              <div className="p-3 bg-[var(--text-secondary)]/10 text-[var(--text-secondary)] rounded-xl group-hover:bg-[var(--accent-color)]/20 group-hover:text-[var(--accent-color)] transition-colors">
                <Mail className="w-5 h-5" />
              </div>
              <div className="text-left">
                <div className="font-bold text-sm text-[var(--text-primary)]">Contact Us</div>
                <div className="text-[10px] text-[var(--text-secondary)] uppercase font-black tracking-widest">Questions or Comments?</div>
              </div>
            </button>

            <button
              onClick={logout}
              className="w-full flex items-center gap-4 p-4 hover:bg-red-500/10 rounded-2xl transition-all group mt-auto"
            >
              <div className="p-3 bg-red-500/10 text-red-500 rounded-xl group-hover:bg-red-500/20">
                <LogOut className="w-5 h-5" />
              </div>
              <div className="text-left">
                <div className="font-bold text-sm text-red-500">Logout</div>
                <div className="text-[10px] text-red-400 uppercase font-black tracking-widest">Sign out of account</div>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* OVERLAY */}
      {isDrawerOpen && <div className="fixed inset-0 bg-black/10 backdrop-blur-[2px] z-[90] transition-opacity duration-500" onClick={() => setIsDrawerOpen(false)} />}

      {/* FLOATING HEADER */}
      <header className="fixed top-2 sm:top-4 inset-x-0 mx-auto w-[98%] sm:w-[95%] max-w-7xl glass-panel px-3 sm:px-6 py-2.5 sm:py-3 rounded-2xl sm:rounded-[2rem] flex justify-between items-center z-50 animate-enter">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setIsDrawerOpen(true)}
            className="p-3 hover:bg-[var(--text-secondary)]/10 rounded-2xl text-[var(--text-secondary)] transition-all active:scale-90"
          >
            <Menu className="w-6 h-6" />
          </button>
          <div className="flex flex-col">
            <h1 className="text-xl font-black tracking-tight text-[var(--text-primary)] flex items-center gap-2">
              <img src="/logo.png" alt="Cathy's Sous Chef" className="w-8 h-8 rounded-lg shadow-sm" />
              <span>Cathy's Sous Chef</span>
            </h1>
          </div>
        </div>

        <div className="flex items-center gap-4">
          {/* MACRO PILLS - DESKTOP */}
          <div className="hidden lg:flex gap-3 mr-4">
            {[
              { label: 'CAL', val: macros.calories, color: 'text-blue-500' },
              { label: 'PRO', val: `${macros.protein} g`, color: 'text-emerald-500' },
              { label: 'CAR', val: `${macros.carbs} g`, color: 'text-amber-500' },
              { label: 'FAT', val: `${macros.fat} g`, color: 'text-rose-500' }
            ].map(p => (
              <button
                key={p.label}
                onClick={() => setView('macros')}
                className="bg-[var(--glass-surface)] border border-[var(--glass-border)] shadow-sm px-4 py-2 rounded-2xl flex items-center gap-2 hover:scale-105 transition-transform"
              >
                <span className="text-[10px] font-black text-[var(--text-secondary)] opacity-70">{p.label}</span>
                <span className={`text-xs font-bold ${p.color}`}>{p.val}</span>
              </button>
            ))}

            {/* TIER BADGE - V6.1-P9: Loading Skeleton */}
            {profileLoading ? (
              <div className="h-8 w-24 bg-[var(--glass-surface)] border border-[var(--glass-border)] rounded-2xl animate-pulse" />
            ) : (
              <div
                onClick={() => window.location.href = 'https://cathys-sous-chef.com/#pricing'}
                className={`px-4 py-2 rounded-2xl border font-black text-[10px] uppercase tracking-widest flex items-center gap-2 cursor-pointer hover:scale-105 transition-transform ${userTier === 'unlimited' ? 'bg-purple-500/10 border-purple-500/20 text-purple-500' : userTier === 'premium' ? 'bg-amber-500/10 border-amber-500/20 text-amber-500' : userTier === 'pro' ? 'bg-blue-500/10 border-blue-500/20 text-blue-500' : 'bg-gray-500/10 border-gray-500/20 text-gray-500'}`}
              >
                {userTier === 'unlimited' ? <Zap className="w-3 h-3 text-purple-500" /> : userTier === 'premium' ? <Zap className="w-3 h-3 fill-amber-500" /> : userTier === 'pro' ? <Star className="w-3 h-3 text-blue-500 fill-blue-500" /> : <ShieldCheck className="w-3 h-3 text-gray-500" />}
                {userTier}
                <Zap className="w-3 h-3 text-[var(--accent-color)] ml-1" />
              </div>
            )}
          </div>

          {/* THEME TOGGLE */}
          <button
            onClick={() => setIsDarkMode(!isDarkMode)}
            className="p-3 bg-[var(--glass-surface)] border border-[var(--glass-border)] rounded-2xl text-[var(--text-secondary)] hover:text-[var(--accent-color)] hover:shadow-lg transition-all active:rotate-45"
          >
            {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>
        </div>
      </header>

      {/* MAIN CONTAINER */}
      <main className="max-w-7xl mx-auto px-6 pt-28 pb-16">
        {/* NAV DOCK */}
        <div className="flex justify-center mb-8 animate-enter" style={{ animationDelay: '0.1s' }}>
          <div className="glass-panel p-1.5 sm:p-2 rounded-full flex gap-1 sm:gap-2">
            {[
              { id: 'current', label: 'Plan', icon: UtensilsCrossed },
              { id: 'inventory', label: 'Kitchen', icon: Package },
              { id: 'history', label: 'History', icon: History },
              { id: 'favorites', label: 'Favorites', icon: Heart },
              { id: 'wine', label: 'Cellar', icon: Wine },
              { id: 'macros', label: 'Preferences', icon: Target },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setView(tab.id)}
                title={tab.label}
                className={`flex items-center justify-center gap-2.5 px-3 sm:px-5 py-2 sm:py-2.5 rounded-full text-sm font-bold transition-all duration-300 ${view === tab.id
                  ? 'bg-[var(--accent-color)] text-white shadow-lg shadow-blue-500/30'
                  : 'text-[var(--text-secondary)] hover:bg-[var(--text-secondary)]/10'
                  }`}
              >
                <tab.icon className={`w-4 h-4 sm:w-4 sm:h-4 ${view === tab.id ? 'text-white' : ''}`} />
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* HERO COMMAND SECTION */}
        <section className="mb-10 animate-enter" style={{ animationDelay: '0.2s' }}>
          <div className="text-center mb-6 px-4">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[var(--text-primary)] to-[var(--text-secondary)] mb-2 tracking-tighter">
              What's the goal today?
            </h2>
            <p className="text-[var(--text-secondary)] font-medium text-base">
              Draft plans, scan groceries, or refine your menu instantly.
            </p>
          </div>

          {/* INVENTORY DRAFT REVIEW (FEAT-003) */}
          {inventoryDraft.length > 0 && (
            <div className="max-w-4xl mx-auto mb-6 px-4">
              <div className="flex items-center justify-between mb-3 px-2">
                <span className="text-xs font-black text-[var(--text-secondary)] uppercase tracking-widest opacity-60">Pending Inventory</span>
                <button
                  onClick={() => setInventoryDraft([])}
                  className="text-xs font-bold text-rose-500 hover:opacity-70 transition-opacity"
                >
                  Clear All
                </button>
              </div>
              <div className="flex gap-3 overflow-x-auto pb-4 no-scrollbar scroll-smooth">
                {inventoryDraft.map((item, idx) => (
                  <div
                    key={`${item.name} -${idx} `}
                    className={`draft-chip group animate-enter ${updatedItems.includes(item.name.toLowerCase()) ? 'draft-chip-pulse animate-jiggle' : ''}`}
                    style={{ animationDelay: `${idx * 0.05} s` }}
                  >
                    <span className="text-blue-500 font-black text-xs">{item.qty}</span>
                    <span className="text-[var(--text-primary)] font-bold text-sm capitalize">{item.name}</span>
                    <button
                      onClick={(e) => { e.stopPropagation(); removeInventoryFromDraft(item.name); }}
                      className="ml-1 opacity-0 group-hover:opacity-100 text-rose-500 hover:scale-125 transition-all"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>

              <div className="flex justify-center mt-4">
                <button
                  onClick={handleStockKitchen}
                  disabled={loading}
                  className={`group relative bg-blue-600 hover:bg-blue-500 text-white px-8 py-4 rounded-2xl font-black text-sm tracking-widest uppercase shadow-xl hover:shadow-blue-500/30 transition-all active:scale-95 flex items-center gap-3 ${fadingId === 'stocking' ? 'animate-fly-to-nav' : ''}`}
                >
                  <Package className="w-5 h-5 group-hover:rotate-12 transition-transform" />
                  Stock Kitchen
                  <div className="absolute inset-0 rounded-2xl ring-4 ring-blue-500/20 animate-pulse" />
                </button>
              </div>
            </div>
          )}

          {/* WINE DRAFT REVIEW (NEW) */}
          {wineDraft.length > 0 && (
            <div className="max-w-4xl mx-auto mb-6 px-4">
              <div className="flex items-center justify-between mb-3 px-2">
                <span className="text-xs font-black text-[var(--text-secondary)] uppercase tracking-widest opacity-60">Pending Cellar Additions</span>
                <button
                  onClick={() => setWineDraft([])}
                  className="text-xs font-bold text-rose-500 hover:opacity-70 transition-opacity"
                >
                  Clear All
                </button>
              </div>
              <div className="flex gap-3 overflow-x-auto pb-4 no-scrollbar scroll-smooth">
                {wineDraft.map((item, idx) => (
                  <div
                    key={`${item.name}-${idx}`}
                    className="draft-chip group animate-enter ring-1 ring-[var(--accent-color)]/20 shadow-sm cursor-pointer hover:ring-[var(--accent-color)] transition-all"
                    style={{ animationDelay: `${idx * 0.05}s` }}
                    onClick={() => setEditingWine(item)}
                  >
                    <Wine className="w-3 h-3 text-[var(--accent-color)]" />
                    <span className="text-[var(--text-primary)] font-bold text-sm capitalize">{item.name}</span>
                    {item.vintage && <span className="text-[10px] text-[var(--text-secondary)] font-black opacity-60">{item.vintage}</span>}
                    {item.sommelier_notes && (
                      <div className="absolute top-full left-0 mt-2 w-48 p-2 bg-[var(--glass-surface)] border border-[var(--glass-border)] rounded-xl text-[10px] text-[var(--text-secondary)] shadow-xl z-50 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                        <span className="font-bold text-[var(--accent-color)] block mb-1">Sommelier Notes:</span>
                        {item.sommelier_notes}
                      </div>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); removeWineFromDraft(idx); }}
                      className="ml-1 opacity-0 group-hover:opacity-100 text-rose-500 hover:scale-125 transition-all"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>

              <div className="flex justify-center mt-4">
                <button
                  onClick={handleStockCellar}
                  disabled={loading}
                  className={`group relative bg-[var(--accent-color)] hover:brightness-110 text-white px-8 py-4 rounded-2xl font-black text-sm tracking-widest uppercase shadow-xl shadow-blue-500/30 transition-all active:scale-95 flex items-center gap-3 ${fadingId === 'stocking-wine' ? 'animate-fly-to-nav' : ''}`}
                >
                  <Wine className="w-5 h-5 group-hover:rotate-12 transition-transform" />
                  Add to Cellar
                  <div className="absolute inset-0 rounded-2xl ring-4 ring-[var(--accent-color)]/20 animate-pulse" />
                </button>
              </div>
            </div>
          )}

          <CommandCenter
            onCommand={handleCommand}
            onScan={handleUnifiedScan}
            isLoading={loading || mealPlanLoading || profileLoading || isContextLoading}
            statusMessage={chefMessage}
            currentView={view}
          />
        </section>

        {/* VIEW AREA */}
        <div className="min-h-[400px]">
          {view === 'current' && (
            <>
              {currentPlan.meals.length > 0 ? (
                <div className="space-y-12 animate-in fade-in slide-in-from-bottom-4 duration-700">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end mb-4 border-b border-[var(--glass-border)] pb-4 gap-4">
                    <div className="flex items-center gap-3">
                      <h3 className="text-2xl font-black text-[var(--text-primary)]">Your Menu</h3>
                      <DietVerificationBadge verification={currentPlan.diet_verification} />
                      {currentPlan.regenerations_remaining !== undefined && (
                        <div className={`px-2 py-0.5 rounded-lg text-[10px] font-black uppercase tracking-wider border ${currentPlan.regenerations_remaining > 0 ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-500' : 'bg-rose-500/10 border-rose-500/20 text-rose-500'}`}>
                          {currentPlan.regenerations_remaining} {currentPlan.regenerations_remaining === 1 ? 'Regen' : 'Regens'} Left
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-4 w-full sm:w-auto">
                      <button
                        onClick={() => {
                          setCurrentPlan({ meals: [], shopping_list: [] });
                          setChefMessage('');
                          triggerToast('Slate cleared! Ask me for a new plan.');
                        }}
                        className="text-sm font-bold flex items-center gap-2 text-[var(--text-secondary)] hover:text-rose-500 transition-all"
                      >
                        <RefreshCcw className="w-4 h-4" /> Start Fresh
                      </button>

                      <button
                        onClick={() => currentPlan.status === 'COMMITTED' ? exportMealPlanToPDF(currentPlan.meals, currentPlan.shopping_list, currentPlan.title) : triggerToast("Commit to this plan to enable PDF Export!")}
                        className={`text-sm font-bold flex items-center gap-2 transition-all ${currentPlan.status === 'COMMITTED' ? 'text-[var(--accent-color)] hover:brightness-110' : 'text-[var(--text-secondary)] opacity-50 cursor-not-allowed'}`}
                      >
                        <FileDown className="w-4 h-4" /> Export PDF
                        {currentPlan.status !== 'COMMITTED' && <Lock className="w-3 h-3" />}
                      </button>
                    </div>
                  </div>

                  {/* Plan Description / Chef's Message */}
                  {(currentPlan.description || currentPlan.chef_message) && (
                    <div className="mb-8 animate-in fade-in slide-in-from-top-2 duration-500">
                      <div className="glass-panel p-6 rounded-2xl border-l-4 border-l-[var(--accent-color)] relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-[var(--accent-color)]/5 rounded-full blur-2xl pointer-events-none -translate-y-1/2 translate-x-1/2" />
                        <h4 className="text-sm font-black text-[var(--text-primary)] mb-2 flex items-center gap-2 relative z-10">
                          <MessageSquareQuote className="w-4 h-4 text-[var(--accent-color)]" />
                          {currentPlan.title || MESSAGES.PLAN_DETAILS.CHEFS_NOTES_TITLE}
                        </h4>
                        <p className="text-sm text-[var(--text-secondary)] italic leading-relaxed relative z-10">
                          {currentPlan.description || currentPlan.chef_message}
                        </p>
                      </div>
                    </div>
                  )}

                  {sortedDays.map(day => (
                    <div key={day} className="space-y-4">
                      <div className="flex items-center gap-4">
                        <span className="text-[11px] font-black text-[var(--text-secondary)]/50 uppercase tracking-[0.3em] whitespace-nowrap">{day}</span>
                        <div className="h-px bg-[var(--glass-border)] w-full" />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
                        {groupedMeals[day].map((meal, idx) => (
                          <div
                            key={idx}
                            onClick={() => setSelectedMeal(meal)}
                            className="meal-card-premium group"
                          >
                            <div className="flex justify-between items-start mb-4">
                              <div className="flex items-center gap-2">
                                <span className="text-[10px] font-black text-[var(--accent-color)] uppercase tracking-widest bg-[var(--accent-color)]/10 px-2 py-1 rounded-lg italic">
                                  {meal.meal_type}
                                </span>
                                {meal.servings > 1 && (
                                  <span className="text-[10px] font-black text-blue-500 uppercase tracking-widest bg-blue-500/10 px-2 py-1 rounded-lg flex items-center gap-1">
                                    <User className="w-3 h-3" />
                                    Serves {meal.servings}
                                  </span>
                                )}
                              </div>
                              <Star className={`w-4 h-4 ${favorites.some(f => f.name === meal.name) ? 'fill-amber-400 text-amber-400' : 'text-[var(--text-secondary)]/30 group-hover:text-[var(--text-secondary)]'} transition-colors`} />
                            </div>
                            <h4 className="text-lg font-bold text-[var(--text-primary)] leading-tight mb-6 group-hover:text-[var(--accent-color)] transition-colors">{meal.name}</h4>
                            {meal.wine_pairing && meal.wine_pairing.name && (
                              (() => {
                                const wp = meal.wine_pairing;
                                const wineType = (wp.wine_type || '').toLowerCase();
                                const isExcluded = wp.selected === false;
                                const isSuggestion = wp.shopping_list_add;
                                const isWhite = wineType === 'white' || wineType === 'sparkling';
                                const isRose = wineType === 'rosé' || wineType === 'rose';

                                let styleClass = "";
                                let iconColor = "";

                                if (isExcluded) {
                                  styleClass = "bg-gray-500/10 border-gray-500/20 text-gray-400 opacity-50";
                                  iconColor = "text-gray-400";
                                } else {
                                  if (isRose) {
                                    styleClass = "bg-pink-400/10 border-pink-400/30 text-pink-600";
                                    iconColor = "text-pink-500";
                                  } else if (isWhite) {
                                    styleClass = "bg-amber-300/10 border-amber-300/30 text-amber-700";
                                    iconColor = "text-amber-500";
                                  } else {
                                    styleClass = "bg-rose-500/10 border-rose-500/20 text-rose-600";
                                    iconColor = "text-rose-500";
                                  }

                                  if (isSuggestion) {
                                    styleClass += " animate-recommendation-glow border-amber-500/30";
                                  }
                                }

                                const isCommitted = currentPlan.status === 'COMMITTED';

                                return (
                                  isCommitted && isExcluded ? null : (
                                    <div
                                      className={`mt-2 flex items-center gap-2 px-3 py-1 border rounded-xl w-fit transition-all ${isCommitted ? '' : 'cursor-pointer'} ${styleClass}`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (!isCommitted) handleWineToggle(meal);
                                      }}
                                      title={isCommitted ? 'Wine pairing locked' : (isExcluded ? 'Wine excluded from commit' : 'Click to exclude wine')}
                                    >
                                      {isExcluded ? (
                                        <X className="w-3 h-3" />
                                      ) : (
                                        <Wine className={`w-3 h-3 ${iconColor}`} />
                                      )}
                                      <span className="text-[9px] font-black uppercase tracking-widest">{wp.name}</span>
                                    </div>
                                  )
                                );
                              })()
                            )}

                            <div className="flex justify-between items-center border-t border-[var(--glass-border)] pt-4 px-1">
                              <div className="flex flex-col items-center">
                                <span className="text-base font-black text-[var(--text-primary)]">{meal.calories || 0}</span>
                                <span className="text-[10px] font-bold text-[var(--text-secondary)] uppercase">Kcal</span>
                              </div>
                              <div className="flex flex-col items-center">
                                <span className="text-base font-black text-[var(--text-primary)]">{meal.protein || 0}g</span>
                                <span className="text-[10px] font-bold text-[var(--text-secondary)] uppercase">PRO</span>
                              </div>
                              <div className="flex flex-col items-center">
                                <span className="text-base font-black text-[var(--text-primary)]">{meal.carbs || 0}g</span>
                                <span className="text-[10px] font-bold text-[var(--text-secondary)] uppercase">CAR</span>
                              </div>
                              <div className="flex flex-col items-center">
                                <span className="text-base font-black text-[var(--text-primary)]">{meal.fat || 0}g</span>
                                <span className="text-[10px] font-bold text-[var(--text-secondary)] uppercase">FAT</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}

                  {/* SHOPPING LIST MINI */}
                  <div className="mt-16 glass-panel rounded-[2rem] sm:rounded-[2.5rem] p-6 sm:p-10 relative overflow-hidden">
                    <div className="relative z-10 flex flex-col md:flex-row gap-6 sm:gap-10">
                      <div className="md:w-1/3 text-center md:text-left">
                        <h3 className="text-2xl sm:text-3xl font-black mb-4 text-[var(--text-primary)]">Shopping List</h3>
                        <p className="text-[var(--text-secondary)] text-sm font-medium mb-4">Everything you need to complete this plan that isn't already in your kitchen.</p>
                        <ShoppingCart className="w-16 h-16 sm:w-20 sm:h-20 text-[var(--text-secondary)]/10 mx-auto md:mx-0" />
                      </div>
                      <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {currentPlan.shopping_list.map((item, idx) => (
                          <div key={idx} className="flex items-center gap-3 bg-[var(--glass-surface)] border border-[var(--glass-border)] p-4 rounded-2xl shadow-sm">
                            <CheckCircle className="w-5 h-5 text-emerald-500 shrink-0" />
                            <span className="text-sm font-bold text-[var(--text-primary)]">
                              {typeof item === 'string' ? item : `${item.qty || ''} ${item.unit || ''} ${item.name || item}`.trim()}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="py-10 flex flex-col items-center justify-center text-center max-w-md mx-auto animate-in fade-in zoom-in-95 duration-1000">
                  <div className="w-20 h-20 bg-[var(--accent-color)]/10 rounded-full flex items-center justify-center mb-4">
                    <img src="/logo.png" alt="Ready" className="w-10 h-10 rounded-xl animate-pulse shadow-lg shadow-[var(--accent-color)]/20" />
                  </div>
                  <h3 className="text-xl font-black text-[var(--text-primary)] mb-2 tracking-tight">Your kitchen is ready.</h3>
                  <p className="text-[var(--text-secondary)] text-sm font-medium leading-relaxed">
                    Type a directive like <span className="text-[var(--accent-color)] font-bold italic">"Plan a 3-day high protein menu"</span> above to get started, or scan your ingredients.
                  </p>
                </div>
              )}
            </>
          )}

          {view === 'macros' && (
            <div className="max-w-4xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-700">
              <div className="text-center mb-10">
                <h3 className="text-3xl font-black text-[var(--text-primary)] tracking-tight mb-2">Targets & Preferences</h3>
                <p className="text-[var(--text-secondary)] font-medium">Customize your goals and food restrictions.</p>
              </div>

              {/* V8: TWO-AXIS DIET PREFERENCES (REPLACES PRESET GALLERY) */}
              <div className="max-w-2xl mx-auto space-y-12 mb-16">
                <DietPhilosophySelector
                  value={dietPhilosophy}
                  onChange={handleDietPhilosophyChange}
                  isLoading={profileLoading}
                />

                {/* V13: HOUSEHOLD SIZE SELECTOR */}
                <div className="bg-[var(--glass-surface)] border border-[var(--glass-border)] rounded-[2rem] p-6 sm:p-8 relative overflow-hidden group">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-[var(--accent-color)]/5 rounded-full blur-2xl pointer-events-none -translate-y-1/2 translate-x-1/2 group-hover:bg-[var(--accent-color)]/10 transition-all duration-700" />

                  <div className="flex flex-col sm:flex-row items-center justify-between gap-6 relative z-10">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-[var(--accent-color)]/10 text-[var(--accent-color)] rounded-2xl">
                        <User className="w-6 h-6" />
                      </div>
                      <div className="text-left">
                        <h4 className="text-lg font-black text-[var(--text-primary)]">Household Size</h4>
                        <p className="text-[10px] text-[var(--text-secondary)] uppercase font-black tracking-widest opacity-60">Scaling for your family</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 bg-[var(--bg-secondary)]/50 p-2 rounded-2xl border border-[var(--glass-border)]">
                      <button
                        onClick={() => handleHouseholdSizeChange(Math.max(1, householdSize - 1))}
                        disabled={householdSize <= 1}
                        className="w-10 h-10 flex items-center justify-center rounded-xl bg-[var(--glass-surface)] border border-[var(--glass-border)] text-[var(--text-primary)] hover:bg-[var(--accent-color)] hover:text-white disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-[var(--text-primary)] transition-all active:scale-90"
                      >
                        <Minus className="w-4 h-4" />
                      </button>

                      <div className="w-12 text-center">
                        <span className="text-xl font-black text-[var(--text-primary)]">{householdSize}</span>
                      </div>

                      <button
                        onClick={() => handleHouseholdSizeChange(Math.min(12, householdSize + 1))}
                        disabled={householdSize >= 12}
                        className="w-10 h-10 flex items-center justify-center rounded-xl bg-[var(--glass-surface)] border border-[var(--glass-border)] text-[var(--text-primary)] hover:bg-[var(--accent-color)] hover:text-white disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-[var(--text-primary)] transition-all active:scale-90"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <p className="text-[10px] text-[var(--text-secondary)] font-medium mt-6 italic text-center sm:text-left">
                    All recipes and nutrition targets will be automatically scaled for a household of {householdSize}.
                  </p>
                </div>

                <MacroProfileSelector
                  value={macroProfile}
                  onChange={handleMacroProfileChange}
                  isLoading={profileLoading}
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                {/* DAILY NUTRITION INPUTS */}
                <div className="glass-panel rounded-[2.5rem] p-8 sm:p-10 shadow-2xl relative overflow-hidden flex flex-col">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-[var(--accent-color)]/5 rounded-full blur-3xl pointer-events-none -translate-y-1/2 translate-x-1/2" />

                  <h4 className="text-xl font-black text-[var(--text-primary)] mb-4 flex items-center gap-2">
                    <Target className="w-5 h-5 text-[var(--accent-color)]" />
                    Daily Nutrition
                  </h4>
                  <p className="text-sm text-[var(--text-secondary)] mb-8 font-medium italic">Adjust target macros for your ideal daily intake.</p>

                  <div className="grid grid-cols-2 gap-6 mb-8">
                    {[
                      { label: 'Calories', key: 'calories', unit: 'Kcal' },
                      { label: 'Protein', key: 'protein', unit: 'g' },
                      { label: 'Carbs', key: 'carbs', unit: 'g' },
                      { label: 'Fat', key: 'fat', unit: 'g' },
                    ].map(field => (
                      <div key={field.key} className="group">
                        <label className="block text-[10px] font-black text-[var(--text-secondary)] uppercase tracking-[0.2em] mb-2 ml-1 group-focus-within:text-[var(--accent-color)] transition-colors">
                          {field.label}
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            value={tempMacros[field.key] ?? macros[field.key]}
                            onChange={(e) => {
                              setTempMacros({ ...tempMacros, [field.key]: parseInt(e.target.value) || 0 });
                              if (macroProfile) handleMacroProfileChange(null); // Clear profile on manual edit
                            }}
                            className="input-premium w-full text-lg py-3"
                          />
                          <span className="absolute right-6 top-1/2 -translate-y-1/2 text-[10px] font-black text-[var(--text-secondary)] uppercase tracking-widest opacity-50">
                            {field.unit}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={handleUpdateMacros}
                    disabled={actionLoading === 'macros'}
                    className="w-full bg-[var(--accent-color)] text-white py-4 rounded-2xl font-black text-base hover:brightness-110 hover:shadow-lg hover:shadow-blue-500/20 transition-all active:scale-95 flex items-center justify-center"
                  >
                    {actionLoading === 'macros' ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <CheckCircle className="w-5 h-5 mr-2" />}
                    Update Goals
                  </button>
                </div>

                {/* FOOD EXCLUSIONS */}
                <div className="glass-panel rounded-[2.5rem] p-8 sm:p-10 shadow-2xl relative overflow-hidden flex flex-col">
                  <h4 className="text-xl font-black text-[var(--text-primary)] mb-4 flex items-center gap-2">
                    <X className="w-5 h-5 text-rose-500" />
                    Food Exclusions
                  </h4>
                  <p className="text-sm text-[var(--text-secondary)] mb-8 font-medium italic">Ingredients you never want the Sous Chef to suggest.</p>

                  <div className="flex-1 space-y-6">
                    <div className="flex flex-wrap gap-3">
                      {exclusions.map((item) => (
                        <div
                          key={item}
                          className={`exclusion-chip animate-pop-in ${fadingId === item ? 'opacity-0 scale-125' : ''}`}
                        >
                          <span className="text-sm font-bold capitalize text-[var(--text-primary)]">{item}</span>
                          <button
                            onClick={() => handleRemoveExclusion(item)}
                            className="p-1 hover:bg-rose-500/20 rounded-full text-[var(--text-secondary)] hover:text-rose-500 transition-colors"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                      {exclusions.length === 0 && (
                        <div className="py-8 w-full text-center border-2 border-dashed border-[var(--glass-border)] rounded-2xl">
                          <p className="text-[var(--text-secondary)] text-xs font-bold uppercase tracking-widest opacity-50">No exclusions active</p>
                        </div>
                      )}
                    </div>

                    <div className="mt-auto pt-6">
                      <div className="relative group">
                        <input
                          type="text"
                          placeholder="Allergic to shellfish? No pork?"
                          value={newExclusion}
                          onChange={(e) => setNewExclusion(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleAddExclusion()}
                          className="input-premium w-full pl-12 pr-12"
                        />
                        <Plus className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--text-secondary)]" />
                        <button
                          onClick={handleAddExclusion}
                          className="absolute right-3 top-1/2 -translate-y-1/2 p-2 bg-[var(--accent-color)] text-white rounded-xl hover:brightness-110 active:scale-95 transition-all"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                      <p className="text-[10px] text-center text-[var(--text-secondary)] mt-4 font-bold uppercase tracking-widest opacity-60">
                        You can also tell the Chef: "Stop suggesting pork"
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* INTELLIGENCE LAYERS & VARIETY */}
              <div className="mt-16 mb-20">
                <div className="flex items-center gap-3 mb-8">
                  <span className="p-2 bg-[var(--accent-color)]/10 rounded-xl text-[var(--accent-color)]">
                    <ShieldCheck className="w-6 h-6" />
                  </span>
                  <div>
                    <h4 className="text-xl font-black text-[var(--text-primary)]">Intelligence Layers</h4>
                    <p className="text-xs text-[var(--text-secondary)] font-medium">Configure advanced Sous Chef capabilities.</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                  {[
                    { id: 'pantry', label: 'Pantry Intelligence', icon: Package, desc: 'Chef analyzes scans.' },
                    { id: 'macro', label: 'Macro Enforcement', icon: Target, desc: 'Chef respects targets.' },
                    { id: 'wine', label: 'Wine Sommelier', icon: Wine, desc: 'Chef suggests pairings.' },
                  ].map((layer) => (
                    <div
                      key={layer.id}
                      onClick={() => handleTogglePreference(layer.id)}
                      className={`glass-panel p-6 rounded-3xl flex flex-col gap-4 cursor-pointer transition-all duration-300 group hover:scale-[1.02] ${toggles[layer.id] ? 'ring-2 ring-[var(--accent-color)]/30' : 'opacity-60 grayscale-[0.5]'}`}
                    >
                      <div className="flex justify-between items-start">
                        <div className={`p-3 rounded-2xl ${toggles[layer.id] ? 'bg-[var(--accent-color)]/10 text-[var(--accent-color)]' : 'bg-gray-500/10 text-gray-400'} transition-colors`}>
                          <layer.icon className="w-6 h-6" />
                        </div>
                        <div className={`w-12 h-6 rounded-full relative transition-colors duration-500 ${toggles[layer.id] ? 'bg-blue-500' : 'bg-gray-500/30'}`}>
                          <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all duration-500 ${toggles[layer.id] ? 'left-7' : 'left-1'}`} />
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-bold text-[var(--text-primary)]">{layer.label}</span>
                          {layer.id === 'wine' && userTier !== 'premium' && <Lock className="w-3 h-3 text-[var(--text-secondary)] opacity-40" />}
                        </div>
                        <p className="text-[10px] text-[var(--text-secondary)] font-black uppercase tracking-widest">{layer.desc}</p>
                      </div>

                      {/* FEAT-WINE-003: Contextual Configurator Chips */}
                      {layer.id === 'wine' && (
                        <div className={`mt-2 flex flex-wrap gap-2 transition-all duration-500 origin-top ${toggles.wine ? 'max-h-20 opacity-100 translate-y-0 scale-100' : 'max-h-0 opacity-0 -translate-y-4 scale-95 pointer-events-none'}`}>
                          {['Breakfast', 'Lunch', 'Dinner'].map(mealType => (
                            <button
                              key={mealType}
                              onClick={(e) => { e.stopPropagation(); handleWineSettingToggle(mealType); }}
                              className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all duration-300 border ${wineSettings[mealType] ? 'bg-orange-500/10 text-orange-500 border-orange-500/30' : 'bg-transparent text-[var(--text-secondary)] border-[var(--glass-border)] opacity-60'}`}
                            >
                              {mealType}
                            </button>
                          ))}
                        </div>
                      )}

                      {/* FEAT-STRUCTURE: Meal Selection Chips */}
                      {layer.id === 'macro' && (
                        <div className={`mt-2 flex flex-wrap gap-2 transition-all duration-500 origin-top ${toggles.macro ? 'max-h-20 opacity-100 translate-y-0 scale-100' : 'max-h-0 opacity-0 -translate-y-4 scale-95 pointer-events-none'}`}>
                          {['Breakfast', 'Lunch', 'Dinner', 'Snack'].map(mealType => (
                            <button
                              key={mealType}
                              onClick={(e) => { e.stopPropagation(); handleMealSettingToggle(mealType); }}
                              className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all duration-300 border ${mealSettings[mealType] ? 'bg-orange-500/10 text-orange-500 border-orange-500/30' : 'bg-transparent text-[var(--text-secondary)] border-[var(--glass-border)] opacity-60'}`}
                            >
                              {mealType}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div className="variety-divider" />

                <div className="max-w-2xl mx-auto">
                  <VarietyModeSelector
                    value={varietyMode}
                    onChange={handleVarietyModeChange}
                    isLoading={varietyModeSaving}
                  />
                </div>
              </div>
            </div>
          )}

          {view === 'inventory' && (
            <div className="max-w-4xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-700">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-12 border-b border-[var(--glass-border)] pb-8">
                <div>
                  <h3 className="text-3xl font-black text-[var(--text-primary)] tracking-tight flex items-center gap-3">
                    <Package className="w-8 h-8 text-[var(--accent-color)]" />
                    Kitchen Stock
                  </h3>
                  <p className="text-[var(--text-secondary)] mt-2 font-medium">Manage your ingredients and scan new items.</p>
                </div>

                <div className="flex items-center gap-4 w-full md:w-auto">
                  <div className="relative group">
                    <input
                      type="file"
                      onChange={(e) => handleUnifiedScan(Array.from(e.target.files))}
                      className="absolute inset-0 opacity-0 cursor-pointer z-10"
                    />
                    <button className="flex items-center gap-2 bg-[var(--accent-color)] text-white px-6 py-3 rounded-2xl font-black shadow-xl shadow-blue-500/30 hover:brightness-110 transition-all active:scale-95">
                      <Camera className="w-5 h-5" />
                      Scan Groceries
                    </button>
                  </div>
                </div>
              </div>

              {/* SMART ADDER BAR */}
              <div className="glass-panel p-6 rounded-[2rem] mb-12">
                <div className="flex gap-4">
                  <div className="flex-1 relative">
                    <input
                      type="text"
                      placeholder="Add something smart (e.g. Pasta - 3 boxes)"
                      className="input-premium w-full pl-12"
                      value={newItem.name}
                      onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddItem()}
                    />
                    <Plus className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--text-secondary)]" />
                  </div>
                  <button
                    onClick={handleAddItem}
                    disabled={isAdding}
                    className="bg-[var(--accent-color)] text-white px-8 rounded-2xl font-black hover:brightness-110 hover:shadow-lg hover:shadow-blue-500/30 transition-all flex items-center justify-center disabled:opacity-50"
                  >
                    {isAdding ? <Loader2 className="w-5 h-5 animate-spin" /> : "Add Item"}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {isContextLoading ? (
                  <div className="col-span-full py-24 text-center">
                    <div className="inline-flex p-6 bg-[var(--glass-surface)] rounded-full mb-4">
                      <Loader2 className="w-12 h-12 text-[var(--accent-color)] animate-spin" />
                    </div>
                    <p className="text-[var(--text-secondary)] font-medium italic">Stocking your pantry...</p>
                  </div>
                ) : inventory.length === 0 ? (
                  <div className="col-span-full py-24 text-center">
                    <div className="inline-flex p-6 bg-[var(--glass-surface)] rounded-full mb-4">
                      <Package className="w-12 h-12 text-[var(--text-secondary)]" />
                    </div>
                    <p className="text-[var(--text-secondary)] font-medium italic">Your kitchen is empty. Try scanning or adding manually!</p>
                  </div>
                ) : (
                  inventory.map((item, idx) => (
                    <div
                      key={idx}
                      className={`group flex justify-between items-center p-5 glass-panel rounded-[1.5rem] hover:scale-[1.02] transition-all duration-300 ${fadingInventoryItem === item.name ? 'opacity-0 scale-95 translate-x-4' : 'opacity-100 scale-100'}`}
                    >
                      <div className="flex flex-col">
                        <span className="font-bold text-[var(--text-primary)]">{item.name}</span>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-[10px] font-black text-[var(--accent-color)] uppercase tracking-widest bg-[var(--accent-color)]/10 px-2 py-0.5 rounded-md">{item.qty}</span>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDeleteItem(item.name)}
                        className="p-3 text-[var(--text-secondary)] hover:text-red-500 hover:bg-red-500/10 rounded-xl transition-all"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}


          {view === 'history' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
              {isContextLoading ? (
                <div className="col-span-full py-20 text-center text-[var(--text-secondary)] italic flex flex-col items-center gap-3">
                  <Loader2 className="w-8 h-8 animate-spin text-[var(--accent-color)]" />
                  Fetching your history...
                </div>
              ) : history.length === 0 ? (
                <div className="col-span-full py-20 text-center text-[var(--text-secondary)]/50 italic">No historical plans found.</div>
              ) : (
                history.map(plan => (
                  <div
                    key={plan.id}
                    onClick={() => loadHistoricalPlan(plan)}
                    className="meal-card-premium group"
                  >
                    <div className="flex justify-between items-start mb-6">
                      <div className="flex flex-col">
                        <span className="text-[10px] font-black text-[var(--text-secondary)] uppercase tracking-widest">{new Date(plan.created_at).toLocaleDateString()}</span>
                        <h4 className="text-xl font-black text-[var(--text-primary)] mt-1">{plan.plan_data?.title || `Plan for ${plan.week_start_date}`}</h4>
                      </div>
                      <History className="w-5 h-5 text-[var(--text-secondary)]/30" />
                    </div>
                    <div className="mb-8">
                      <p className="text-sm text-[var(--text-secondary)] font-medium italic line-clamp-3">
                        {plan.plan_data?.description || (plan.plan_data?.chef_message
                          ? `"${plan.plan_data.chef_message}"`
                          : `${plan.plan_data?.meals?.length || 0} meals custom tailored for your goals.`)}
                      </p>
                    </div>
                    <div className="flex justify-between items-center pt-6 border-t border-[var(--glass-border)]">
                      <span className="text-sm font-bold text-[var(--accent-color)]">View Plan</span>
                      <button
                        onClick={(e) => wrappedHandleDeleteHistory(e, plan.id)}
                        className="p-2 text-[var(--text-secondary)] hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all transform hover:scale-110"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {view === 'favorites' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
              {isContextLoading ? (
                <div className="col-span-full py-20 text-center text-[var(--text-secondary)] italic flex flex-col items-center gap-3">
                  <Loader2 className="w-8 h-8 animate-spin text-[var(--accent-color)]" />
                  Fetching your favorites...
                </div>
              ) : favorites.length === 0 ? (
                <div className="col-span-full py-20 text-center text-[var(--text-secondary)]/50 italic">No favorite recipes yet.</div>
              ) : (
                favorites.map(fav => (
                  <div
                    key={fav.id}
                    onClick={async () => {
                      try {
                        let fullFav = fav;
                        if (!fav.instructions) {
                          const token = await currentUser.getIdToken();
                          fullFav = await loadFavoriteDetails(token, fav.id);
                        }
                        setSelectedMeal({ ...fullFav, calories: fullFav.macros.calories, protein: fullFav.macros.protein, carbs: fullFav.macros.carbs, fat: fullFav.macros.fat });
                      } catch (err) {
                        triggerToast("Failed to load recipe details.");
                      }
                    }}
                    className="meal-card-premium group"
                  >
                    <div className="flex justify-between items-start mb-10">
                      <h4 className="text-xl font-black text-[var(--text-primary)] group-hover:text-[var(--accent-color)] transition-colors leading-tight">{fav.name}</h4>
                      <Bookmark className="w-5 h-5 text-[var(--accent-color)] fill-[var(--accent-color)]" />
                    </div>
                    <div className="flex justify-between items-center border-t border-[var(--glass-border)] pt-6 px-1">
                      <div className="flex flex-col items-center">
                        <span className="text-base font-black text-[var(--text-primary)]">{fav.macros.calories}</span>
                        <span className="text-[10px] font-bold text-[var(--text-secondary)] uppercase">Kcal</span>
                      </div>
                      <div className="flex flex-col items-center">
                        <span className="text-base font-black text-[var(--text-primary)]">{fav.macros.protein}g</span>
                        <span className="text-[10px] font-bold text-[var(--text-secondary)] uppercase">PRO</span>
                      </div>
                      <div className="flex flex-col items-center">
                        <span className="text-base font-black text-[var(--text-primary)]">{fav.macros.carbs}g</span>
                        <span className="text-[10px] font-bold text-[var(--text-secondary)] uppercase">CAR</span>
                      </div>
                      <div className="flex flex-col items-center">
                        <span className="text-base font-black text-[var(--text-primary)]">{fav.macros.fat}g</span>
                        <span className="text-[10px] font-bold text-[var(--text-secondary)] uppercase">FAT</span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {view === 'wine' && (
            <div className="max-w-4xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-700">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-12 border-b border-[var(--glass-border)] pb-8">
                <div>
                  <h3 className="text-3xl font-black text-[var(--text-primary)] tracking-tight flex items-center gap-3">
                    <Wine className="w-8 h-8 text-[var(--accent-color)]" />
                    Digital Cellar
                  </h3>
                  <p className="text-[var(--text-secondary)] mt-2 font-medium">Manage your private stock and pairings.</p>
                </div>

                <div className="flex items-center gap-4 w-full md:w-auto">
                  <div className="relative group">
                    <input
                      type="file"
                      onChange={(e) => handleWineScan(e.target.files[0])}
                      className="absolute inset-0 opacity-0 cursor-pointer z-10"
                    />
                    <button disabled={isWineScanning} className="flex items-center gap-2 bg-[var(--accent-color)] text-white px-6 py-3 rounded-2xl font-black shadow-xl shadow-blue-500/30 hover:brightness-110 transition-all active:scale-95 disabled:opacity-50">
                      {isWineScanning ? <Loader2 className="w-5 h-5 animate-spin" /> : <Scan className="w-5 h-5" />}
                      Scan Label
                    </button>
                  </div>
                </div>
              </div>

              {/* WINE ADDER BAR - Mirrored from Inventory */}
              <div className="glass-panel p-6 rounded-[2rem] mb-12">
                <div className="flex gap-4">
                  <div className="flex-1 relative">
                    <input
                      type="text"
                      placeholder="Add manually (e.g. Cabernet Sauvignon 2018)"
                      className="input-premium w-full pl-12"
                      value={newWine}
                      onChange={(e) => setNewWine(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddWine()}
                    />
                    <Plus className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--text-secondary)]" />
                  </div>
                  <button
                    onClick={handleAddWine}
                    disabled={isAddingWine}
                    className="bg-[var(--accent-color)] text-white px-8 rounded-2xl font-black hover:brightness-110 hover:shadow-lg hover:shadow-blue-500/30 transition-all flex items-center justify-center disabled:opacity-50"
                  >
                    {isAddingWine ? <Loader2 className="w-5 h-5 animate-spin" /> : "Add Item"}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
                {isContextLoading ? (
                  <div className="col-span-full py-24 text-center">
                    <div className="inline-flex p-6 bg-[var(--accent-color)]/5 rounded-full mb-4">
                      <Loader2 className="w-12 h-12 text-[var(--accent-color)] animate-spin" />
                    </div>
                    <p className="text-[var(--text-secondary)] font-medium italic">Stocking your cellar...</p>
                  </div>
                ) : wineCellar.length === 0 ? (
                  <div className="col-span-full py-24 text-center">
                    <div className="inline-flex p-6 bg-[var(--accent-color)]/5 rounded-full mb-4">
                      <Wine className="w-12 h-12 text-[var(--accent-color)]/30" />
                    </div>
                    <p className="text-[var(--text-secondary)] font-medium italic">Your cellar is empty. Try scanning or adding manually!</p>
                  </div>
                ) : (
                  wineCellar.map((wine) => (
                    <div
                      key={wine.id}
                      onClick={() => setEditingWine(wine)}
                      className="group flex justify-between items-center p-5 glass-panel rounded-[1.5rem] hover:scale-[1.02] transition-all duration-300 animate-pop-in cursor-pointer hover:ring-1 hover:ring-[var(--accent-color)]/30"
                    >
                      <div className="flex flex-col">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-[var(--text-primary)]">{wine.name}</span>
                          <span className={`text-[9px] font-black uppercase tracking-[0.2em] px-2 py-0.5 rounded-md ${wine.wine_type?.toLowerCase() === 'white' ? 'bg-amber-100 text-amber-800' : 'bg-rose-900/20 text-rose-600'}`}>
                            {wine.wine_type}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-[10px] font-black text-[var(--text-secondary)] uppercase tracking-widest">{wine.varietal}</span>
                          <span className="text-[10px] font-bold text-[var(--text-secondary)]/50">•</span>
                          <span className="text-[10px] font-bold text-[var(--text-secondary)]">{wine.region}</span>
                          <span className="text-[10px] font-bold text-[var(--text-secondary)]/50">•</span>
                          <span className="text-[10px] font-bold text-[var(--text-secondary)]">{wine.vintage}</span>
                          {wine.quantity > 1 && (
                            <>
                              <span className="text-[10px] font-bold text-[var(--text-secondary)]/50">•</span>
                              <span className="text-[10px] font-black text-[var(--accent-color)] bg-[var(--accent-color)]/10 px-1.5 rounded">x{wine.quantity}</span>
                            </>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteWine(wine.id); }}
                        className="p-3 text-[var(--text-secondary)] hover:text-red-500 hover:bg-red-500/10 rounded-xl transition-all"
                      >
                        {wineUpdateLoading === wine.id ? <Loader2 className="w-5 h-5 animate-spin" /> : <Trash2 className="w-5 h-5" />}
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {view === 'contact' && (
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-700">
              <Contact />
            </div>
          )}
        </div>
      </main >



      <WineEditModal
        isOpen={!!editingWine}
        onClose={() => setEditingWine(null)}
        wine={editingWine}
        onSave={handleSaveWine}
        isProcessing={isSavingWine}
      />

      {/* RECIPE MODAL OVERHAUL - V4.4 Fixed Scroll & Markdown */}
      {
        liveSelectedMeal && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-md z-[200] flex items-center justify-center p-4" onClick={() => setSelectedMeal(null)}>
            <div className="glass-panel rounded-[2.5rem] max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl animate-enter flex flex-col" onClick={e => e.stopPropagation()}>
              <div className="flex-none p-6 pb-2 flex justify-between items-start bg-[var(--card-surface)]">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[10px] font-black text-[var(--accent-color)] uppercase tracking-widest bg-[var(--accent-color)]/10 px-2 py-1 rounded-lg italic">{liveSelectedMeal.meal_type || 'Recipe'}</span>
                    {liveSelectedMeal.servings > 1 && (
                      <span className="text-[10px] font-black text-blue-500 uppercase tracking-widest bg-blue-500/10 px-2 py-1 rounded-lg flex items-center gap-1">
                        <User className="w-3 h-3" />
                        Serves {liveSelectedMeal.servings}
                      </span>
                    )}
                    <span className="text-[10px] font-black text-[var(--text-secondary)] uppercase tracking-widest">{liveSelectedMeal.day}</span>
                  </div>
                  <h2 className="text-2xl font-black text-[var(--text-primary)] leading-tight">{liveSelectedMeal.name}</h2>
                </div>
                <button onClick={() => setSelectedMeal(null)} className="p-2 bg-[var(--text-secondary)]/10 border border-[var(--glass-border)] rounded-xl hover:bg-[var(--text-secondary)]/20 transition-all">
                  <X className="w-5 h-5 text-[var(--text-secondary)]" />
                </button>
              </div>

              <div className="flex-none px-6 pb-4 border-b border-[var(--glass-border)] bg-[var(--card-surface)]">
                <div className="grid grid-cols-4 gap-2 bg-[var(--glass-surface)] p-3 rounded-2xl border border-[var(--glass-border)]">
                  {[
                    { l: 'Kcal', v: liveSelectedMeal.calories, total: liveSelectedMeal.macros_total?.calories },
                    { l: 'PRO', v: `${liveSelectedMeal.protein}g`, total: liveSelectedMeal.macros_total ? `${liveSelectedMeal.macros_total.protein}g` : null },
                    { l: 'CARB', v: `${liveSelectedMeal.carbs}g`, total: liveSelectedMeal.macros_total ? `${liveSelectedMeal.macros_total.carbs}g` : null },
                    { l: 'FAT', v: `${liveSelectedMeal.fat}g`, total: liveSelectedMeal.macros_total ? `${liveSelectedMeal.macros_total.fat}g` : null }
                  ].map(m => (
                    <div key={m.l} className="text-center p-1.5 rounded-xl bg-white/5 border border-white/5 flex flex-col justify-center">
                      <p className="text-sm font-black text-[var(--text-primary)] leading-tight">{m.v}</p>
                      {m.total && (
                        <p className="text-[8px] font-bold text-[var(--text-secondary)] opacity-60 leading-none mb-1">Total: {m.total}</p>
                      )}
                      <p className="text-[9px] font-bold text-[var(--text-secondary)] uppercase tracking-wider">{m.l}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-6 py-8 space-y-8 bg-[var(--card-surface)] custom-scrollbar">
                <div className="markdown-content">
                  <h3 className="text-xs font-black text-[var(--text-secondary)] uppercase tracking-widest mb-4 flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[var(--accent-color)]" /> Culinary Instructions
                  </h3>
                  {liveSelectedMeal.instructions ? (
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {liveSelectedMeal.instructions}
                    </ReactMarkdown>
                  ) : (
                    <p className="text-[var(--text-secondary)] italic">The Sous Chef is still preparing the specific steps for this dish...</p>
                  )}
                </div>

                {liveSelectedMeal.wine_pairing && liveSelectedMeal.wine_pairing.name &&
                  (() => {
                    const wineType = (liveSelectedMeal.wine_pairing.wine_type || '').toLowerCase();
                    const isExcluded = liveSelectedMeal.wine_pairing.selected === false;
                    const isSuggestion = liveSelectedMeal.wine_pairing.shopping_list_add;
                    const isWhite = wineType === 'white' || wineType === 'sparkling';
                    const isRose = wineType === 'rosé' || wineType === 'rose';

                    const glassClass = isRose ? 'glass-wine-rose' : isWhite ? 'glass-wine-white' : 'glass-wine-red';
                    const cardClass = isExcluded
                      ? 'bg-gray-500/30 border border-gray-500/20'
                      : glassClass + (isSuggestion ? ' animate-recommendation-glow' : ' shadow-xl');

                    const headerColor = isExcluded ? 'text-gray-400'
                      : isRose ? 'text-pink-300' : isWhite ? 'text-amber-700' : 'text-rose-300';
                    const nameColor = isExcluded ? 'text-gray-300 line-through'
                      : isRose ? 'text-white' : isWhite ? 'text-amber-900' : 'text-white';
                    const descColor = isExcluded ? 'text-gray-400'
                      : isRose ? 'text-pink-100/80' : isWhite ? 'text-amber-800' : 'text-rose-100/80';
                    const iconColor = isExcluded ? 'text-gray-500'
                      : isRose ? 'text-pink-400' : isWhite ? 'text-amber-500' : 'text-rose-500';

                    return (
                      <div className={`p-6 rounded-3xl relative overflow-hidden group transition-all duration-700 ${cardClass}`}>
                        <div className="absolute -right-4 -top-4 opacity-10 group-hover:rotate-12 transition-transform duration-700">
                          <Wine className={`w-20 h-20 ${iconColor}`} />
                        </div>

                        <div className="flex items-center justify-between mb-4 relative z-20">
                          <h3 className={`text-xs font-black uppercase tracking-widest flex items-center gap-2 ${headerColor}`}>
                            {isExcluded ? (
                              <><X className="w-3 h-3" /> Wine Excluded</>
                            ) : isSuggestion ? (
                              <><Wine className="w-3 h-3" /> Sommelier Pick</>
                            ) : (
                              <><CheckCircle className="w-3 h-3" /> Estate Pairing</>
                            )}
                          </h3>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleWineToggle(liveSelectedMeal); }}
                            disabled={!currentPlan?.id}
                            className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${!currentPlan?.id ? 'opacity-50 cursor-not-allowed' : ''} ${isExcluded
                              ? 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                              : 'bg-rose-600 text-white hover:bg-rose-500'
                              }`}
                          >
                            {!currentPlan?.id ? <Loader2 className="w-3 h-3 animate-spin" /> : (isExcluded ? 'Include' : 'Exclude')}
                          </button>
                        </div>

                        <div className={`relative z-10 markdown-content wine-notes ${isExcluded ? 'opacity-50' : ''}`}>
                          <p className={`text-xl font-black mb-2 ${nameColor}`}>{liveSelectedMeal.wine_pairing.name}</p>
                          <div className={descColor}>
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                              {liveSelectedMeal.wine_pairing.reason || "A perfect match selected for this dish."}
                            </ReactMarkdown>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
              </div>

              <div className="flex-none p-6 border-t border-[var(--glass-border)] flex gap-4 bg-[var(--card-surface)]">
                <button
                  onClick={() => handleToggleFavorite(liveSelectedMeal)}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-bold transition-all border ${favorites.some(f => f.name === liveSelectedMeal.name)
                    ? 'bg-amber-500/10 border-amber-500/20 text-amber-500'
                    : 'bg-transparent border-[var(--glass-border)] text-[var(--text-secondary)] hover:bg-[var(--glass-surface)]'
                    }`}
                >
                  <Star className={`w-4 h-4 ${favorites.some(f => f.name === liveSelectedMeal.name) ? 'fill-amber-500' : ''}`} />
                  {favorites.some(f => f.name === liveSelectedMeal.name) ? 'Saved' : 'Save'}
                </button>
                <button onClick={() => setSelectedMeal(null)} className="flex-1 bg-[var(--text-primary)] text-[var(--glass-surface)] py-3 rounded-xl font-bold hover:opacity-90 transition-all shadow-xl">
                  Got it, Chef
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* LIQUID GLASS TOAST */}
      {
        showToast && (
          <div className="fixed top-24 left-1/2 -translate-x-1/2 z-[200] animate-in fade-in slide-in-from-top-2 duration-500">
            <div className="bg-[var(--glass-surface)] backdrop-blur-xl border border-[var(--accent-color)]/30 px-6 py-3 rounded-2xl shadow-2xl flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-emerald-500" />
              <span className="text-sm font-bold text-[var(--text-primary)]">{showToast}</span>
            </div>
          </div>
        )
      }

      {/* VERIFICATION / OVERRIDE PANEL */}
      {
        view === 'current' && (
          <ManualOverridePanel
            isOpen={isOverridePanelOpen}
            onClose={() => setIsOverridePanelOpen(false)}
            plan={currentPlan}
            onCommit={handleCommitWithOverrides}
            isLoading={loading}
          />
        )
      }

      {/* ATOMIC COMMIT BAR - UX: Only show when complete */}
      {
        view === 'current' && (currentPlan.status === 'completed' || currentPlan.status === 'created' || currentPlan.status === 'DRAFT' || currentPlan.status === 'COMMITTED') && (
          <CommitBar
            plan={currentPlan}
            onCommit={handleCommit}
            onOpenOverrides={() => setIsOverridePanelOpen(true)}
            isLoading={loading}
          />
        )
      }

      {/* UPGRADE MODAL */}
      <UpgradeModal
        isOpen={isUpgradeModalOpen}
        onClose={() => setIsUpgradeModalOpen(false)}
        featureName={upgradeFeature}
      />

      <GuideModal
        isOpen={isGuideOpen}
        onClose={() => setIsGuideOpen(false)}
      />
    </div >
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

function AppContent() {
  const { currentUser } = useAuth();
  return currentUser ? <MealPlanner /> : <Login />;
}

export default App;